import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Upload,
  Tag,
  Space,
  Typography,
  Image,
  Popconfirm,
  Select,
  Alert,
  Divider
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import {
  ADVERTISEMENT_INTENT_OPEN_KEY,
  ADVERTISEMENT_INTENT_SLOT_ID_KEY,
  getAdvertisementPageLabel,
  getAdvertisementPositionLabel
} from '../../../utils/advertisementSlots';
import { getAdvertisementImageSrc } from '../../../utils/advertisementImage';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';
import { useDevFakePaymentEnabled } from '../../../hooks/useDevFakePaymentEnabled';
import CreditCard from '../../../components/Common/CreditCard';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import SubscriptionPaymentIssueModal from '../../../components/Common/SubscriptionPaymentIssueModal';
import { getParamPaymentUrl, redirectToParam3d } from '../../../utils/subscriptionPaymentRedirect';
import { isRecoverableSubscriptionPaymentMessage } from '../../../utils/subscriptionRecoverablePaymentMessage';
import TabListLoading from '../../../components/Common/TabListLoading';
import {
  AdvertisementSlotResponse,
  AdvertisementRequestResponse,
  AdvertisementType,
  AdvertisementRequestStatus,
  ApiResponse,
  ParamPaymentInitResponse
} from '../../../types';

const { Text } = Typography;
const { TextArea } = Input;

const AdvertisementTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [selectedSlot, setSelectedSlot] = useState<AdvertisementSlotResponse | null>(null);
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null);
  const [cardNumber, setCardNumber] = useState('');
  const [cardHolderName, setCardHolderName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentIssueOpen, setPaymentIssueOpen] = useState(false);
  const [paymentIssueMessage, setPaymentIssueMessage] = useState('');
  const { data: devFakeOn } = useDevFakePaymentEnabled();

  const { data: slots = [], isLoading: slotsLoading } = useQuery({
    queryKey: mentorProfileQueryKeys.advertisementSlots,
    queryFn: async () => {
      const res = await apiService.getAdvertisementSlots();
      if (!res.success) throw new Error(res.message);
      return res.data;
    },
  });

  const { data: requests = [], isLoading: requestsLoading } = useQuery({
    queryKey: mentorProfileQueryKeys.advertisementRequests,
    queryFn: async () => {
      const res = await apiService.getMyAdvertisementRequests();
      if (!res.success) throw new Error(res.message);
      return res.data;
    },
  });

  const loading = slotsLoading || requestsLoading;

  useEffect(() => {
    if (slotsLoading || slots.length === 0) {
      return;
    }

    const shouldOpen = localStorage.getItem(ADVERTISEMENT_INTENT_OPEN_KEY) === '1';
    const slotIdValue = localStorage.getItem(ADVERTISEMENT_INTENT_SLOT_ID_KEY);
    const slotId = slotIdValue ? Number(slotIdValue) : NaN;

    if (!shouldOpen || Number.isNaN(slotId)) {
      return;
    }

    const targetSlot = slots.find(slot => slot.id === slotId && slot.isActive);
    localStorage.removeItem(ADVERTISEMENT_INTENT_OPEN_KEY);
    localStorage.removeItem(ADVERTISEMENT_INTENT_SLOT_ID_KEY);

    if (!targetSlot) {
      return;
    }

    setModalVisible(true);
    setSelectedSlot(targetSlot);
    setSelectedWeek(null);
    form.setFieldsValue({
      slotId: targetSlot.id,
      requestedWeekStartDate: null,
    });
  }, [form, slots, slotsLoading]);

  useEffect(() => {
    const err = searchParams.get('paymentInputError');
    if (err) {
      setPaymentIssueMessage(decodeURIComponent(err));
      setPaymentIssueOpen(true);
      setModalVisible(true);
      const next = new URLSearchParams(searchParams);
      next.delete('paymentInputError');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const createRequestMutation = useMutation({
    mutationFn: (formData: FormData) => apiService.createAdvertisementRequest(formData),
    onSuccess: (res: ApiResponse<ParamPaymentInitResponse>) => {
      const paymentUrl = getParamPaymentUrl(res);
      const orderId =
        res.data?.orderId ?? (res.data as { OrderId?: string } | undefined)?.OrderId;
      if (res.success && paymentUrl) {
        if (orderId) {
          localStorage.setItem('pendingAdvertisementPaymentOrderId', orderId);
          localStorage.removeItem('pendingPaymentOrderId');
        }
        redirectToParam3d(paymentUrl);
      } else {
        const msg = res.message || 'Ödeme işlemi başlatılamadı.';
        if (isRecoverableSubscriptionPaymentMessage(msg)) {
          setPaymentIssueMessage(msg);
          setPaymentIssueOpen(true);
        } else {
          toast.error(msg);
        }
        setPaymentLoading(false);
      }
    },
    onError: (error: unknown) => {
      const apiMessage = axios.isAxiosError(error)
        ? (error.response?.data as { message?: string } | undefined)?.message
        : undefined;
      const msg = apiMessage || 'Reklam talebi oluşturulurken hata oluştu.';
      if (isRecoverableSubscriptionPaymentMessage(msg)) {
        setPaymentIssueMessage(msg);
        setPaymentIssueOpen(true);
      } else {
        toast.error(msg);
      }
      setPaymentLoading(false);
    },
  });

  const deleteRequestMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteAdvertisementRequest(id),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Reklam talebi silindi.');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.advertisementRequests });
      } else toast.error(res.message || 'Reklam talebi silinirken hata oluştu.');
    },
    onError: () => toast.error('Reklam talebi silinirken hata oluştu.'),
  });

  const handleCreateRequest = async (values: any) => {
    if (!selectedSlot) {
      toast.error('Lütfen bir reklam slotu seçin.');
      return;
    }
    if (!values.image?.file) {
      toast.error('Lütfen bir resim seçin.');
      return;
    }
    if (!values.requestedWeekStartDate) {
      toast.error('Lütfen bir gün seçin.');
      return;
    }
    if (!cardNumber || !cardHolderName || !expiryDate || !cvv) {
      toast.error('Lütfen ödeme bilgilerini girin.');
      return;
    }

    try {
      setPaymentLoading(true);
      const rentalDay = dayjs(values.requestedWeekStartDate);
      
      // Expiry date'i ay ve yıl olarak ayır
      let expiryMonth = '';
      let expiryYear = '';
      if (expiryDate) {
        const parts = expiryDate.split('/');
        if (parts.length === 2) {
          expiryMonth = parts[0].padStart(2, '0');
          expiryYear = parts[1].length === 4 ? parts[1].substring(2) : parts[1];
        }
      }

      const imageFile = values.image?.file?.originFileObj ?? values.image?.file;
      if (!imageFile || !(imageFile instanceof File)) {
        toast.error('Lütfen geçerli bir reklam görseli seçin.');
        setPaymentLoading(false);
        return;
      }
      const formData = new FormData();
      formData.append('AdvertisementSlotId', selectedSlot.id.toString());
      formData.append('Image', imageFile);
      formData.append('RequestedWeekStartDate', rentalDay.format('YYYY-MM-DD'));
      formData.append('CardNumber', cardNumber.replace(/\s/g, ''));
      formData.append('CardHolderName', cardHolderName);
      formData.append('ExpiryMonth', expiryMonth);
      formData.append('ExpiryYear', expiryYear);
      formData.append('CVV', cvv);
      if (values.linkUrl) formData.append('LinkUrl', values.linkUrl);
      if (values.title) formData.append('Title', values.title);
      if (values.description) formData.append('Description', values.description);
      if (values.phoneNumber) formData.append('PhoneNumber', values.phoneNumber);
      
      createRequestMutation.mutate(formData);
    } catch (error) {
      toast.error('Form gönderilirken hata oluştu.');
      setPaymentLoading(false);
    }
  };

  const handleDevFakeRequest = async (values: any) => {
    if (!selectedSlot) {
      toast.error('Lütfen bir reklam slotu seçin.');
      return;
    }
    if (!values.image?.file) {
      toast.error('Lütfen bir resim seçin.');
      return;
    }
    if (!values.requestedWeekStartDate) {
      toast.error('Lütfen bir gün seçin.');
      return;
    }
    try {
      setPaymentLoading(true);
      const rentalDay = dayjs(values.requestedWeekStartDate);
      const imageFile = values.image?.file?.originFileObj ?? values.image?.file;
      if (!imageFile || !(imageFile instanceof File)) {
        toast.error('Lütfen geçerli bir reklam görseli seçin.');
        setPaymentLoading(false);
        return;
      }
      const formData = new FormData();
      formData.append('AdvertisementSlotId', selectedSlot.id.toString());
      formData.append('Image', imageFile);
      formData.append('RequestedWeekStartDate', rentalDay.format('YYYY-MM-DD'));
      if (values.linkUrl) formData.append('LinkUrl', values.linkUrl);
      if (values.title) formData.append('Title', values.title);
      if (values.description) formData.append('Description', values.description);
      if (values.phoneNumber) formData.append('PhoneNumber', values.phoneNumber);

      const res = await apiService.createAdvertisementDevFake(formData);
      if (res.success && res.data) {
        toast.success(res.data.message || res.message || 'Test ödemesi tamamlandı.');
        setModalVisible(false);
        form.resetFields();
        setSelectedSlot(null);
        setSelectedWeek(null);
        setCardNumber('');
        setCardHolderName('');
        setExpiryDate('');
        setCvv('');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.advertisementRequests });
      } else {
        toast.error(res.message || 'Test ödemesi başarısız.');
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Test talebi başarısız.'));
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleDeleteRequest = (id: number) => deleteRequestMutation.mutate(id);

  const getStatusColor = (status: AdvertisementRequestStatus) => {
    switch (status) {
      case AdvertisementRequestStatus.Pending:
        return 'orange';
      case AdvertisementRequestStatus.Approved:
        return 'green';
      case AdvertisementRequestStatus.Rejected:
        return 'red';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Slot',
      dataIndex: 'slotName',
      key: 'slotName',
      render: (text: string, record: AdvertisementRequestResponse) => (
        <Space direction="vertical" size="small">
          <Space>
            <Tag color={record.slotType === AdvertisementType.Banner ? 'blue' : 'purple'}>
              {record.slotType === AdvertisementType.Banner ? 'Banner' : 'Card'}
            </Tag>
              <Tag>{getAdvertisementPositionLabel(record.slotPosition)}</Tag>
            <Text>{text}</Text>
          </Space>
          {record.requestedWeekLabel && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Gün: {record.requestedWeekLabel}
            </Text>
          )}
        </Space>
      )
    },
    {
      title: 'Resim',
      key: 'image',
      width: 100,
      render: (record: AdvertisementRequestResponse) => (
        <Image
          width={80}
          height={50}
          src={getAdvertisementImageSrc(record.imageBase64, record.imageContentType)}
          style={{ objectFit: 'cover', borderRadius: '4px' }}
        />
      )
    },
    {
      title: 'Bağlantı',
      dataIndex: 'linkUrl',
      key: 'linkUrl',
      render: (url: string) => url ? (
        <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
      ) : '-'
    },
    {
      title: 'Yayın Günü',
      dataIndex: 'requestedWeekLabel',
      key: 'requestedWeekLabel',
      render: (label: string) => label || '-'
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: AdvertisementRequestStatus, record: AdvertisementRequestResponse) => (
        <Space direction="vertical" size="small">
          <Tag color={getStatusColor(status)}>{record.statusName}</Tag>
          <Tag color={record.paymentStatus === 'Completed' ? 'green' : record.paymentStatus === 'Failed' ? 'red' : 'orange'}>
            {record.paymentStatus === 'Completed' ? 'Ödeme Tamamlandı' : record.paymentStatus === 'Failed' ? 'Ödeme Başarısız' : 'Ödeme Bekliyor'}
          </Tag>
        </Space>
      )
    },
    {
      title: 'Tutar',
      dataIndex: 'paymentAmount',
      key: 'paymentAmount',
      render: (amount: number) => amount ? `₺${amount.toFixed(2)}` : '-'
    },
    {
      title: 'Oluşturulma',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR')
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (record: AdvertisementRequestResponse) => (
        <Space>
          {record.status === AdvertisementRequestStatus.Pending && (
            <Popconfirm
              title="Bu talebi silmek istediğinize emin misiniz?"
              onConfirm={() => handleDeleteRequest(record.id)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              >
                Sil
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <>
    <PanelTabCard
      title="Reklam Yönetimi"
      description="Reklam taleplerinizi oluşturun ve yönetin."
    >
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalVisible(true)}
        >
          Yeni Reklam Talebi
        </Button>
      </div>
      <TabListLoading loading={loading}>
      <Card className="panel-table-card">
        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={requests}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>
      </TabListLoading>
    </PanelTabCard>

      <Modal
        title="Yeni Reklam Talebi"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setSelectedSlot(null);
          setSelectedWeek(null);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateRequest}
        >
          <Form.Item
            label="Reklam Slotu"
            name="slotId"
            rules={[{ required: true, message: 'Lütfen bir reklam slotu seçin' }]}
          >
            <Select
              placeholder="Reklam slotu seçin"
              onChange={(value) => {
                const slot = slots.find(s => s.id === value);
                setSelectedSlot(slot || null);
                setSelectedWeek(null);
                form.setFieldValue('requestedWeekStartDate', null);
              }}
            >
              {slots
                .filter(s => s.isActive)
                .map(slot => (
                  <Select.Option key={slot.id} value={slot.id}>
                    {slot.typeName} / {getAdvertisementPositionLabel(slot.position)} / {getAdvertisementPageLabel(slot.pagePath)} - {slot.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          {selectedSlot && (
            <Form.Item
              label="Yayın Günü"
              name="requestedWeekStartDate"
              rules={[{ required: true, message: 'Lütfen bir gün seçin' }]}
              extra={
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    <strong>{selectedSlot.availableWeeks.filter(w => w.isAvailable).length}</strong> gün müsait,
                    <strong> {selectedSlot.availableWeeks.filter(w => !w.isAvailable).length}</strong> gün dolu (önümüzdeki {selectedSlot.availableWeeks.length} gün)
                  </Text>
                </div>
              }
            >
              <Select
                placeholder="Gün seçin"
                onChange={(value) => {
                  setSelectedWeek(value);
                }}
                showSearch
                filterOption={(input, option) => {
                  const label = typeof option?.label === 'string' ? option.label : 
                                typeof option?.children === 'string' ? option.children : '';
                  return label.toLowerCase().includes(input.toLowerCase());
                }}
                style={{ width: '100%' }}
              >
                {selectedSlot.availableWeeks.map(week => (
                  <Select.Option 
                    key={week.weekStartDate} 
                    value={week.weekStartDate}
                    disabled={!week.isAvailable}
                  >
                    <Space>
                      <span>{week.weekLabel}</span>
                      {week.isAvailable ? (
                        <Tag color="green" style={{ margin: 0 }}>Müsait</Tag>
                      ) : (
                        <Tag color="red" style={{ margin: 0 }}>Dolu</Tag>
                      )}
                    </Space>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item
            label="Reklam Resmi"
            name="image"
            rules={[{ required: true, message: 'Lütfen bir resim yükleyin' }]}
          >
            <Upload
              listType="picture-card"
              maxCount={1}
              beforeUpload={() => false}
              accept="image/*"
            >
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Yükle</div>
              </div>
            </Upload>
          </Form.Item>

          {selectedSlot?.type === AdvertisementType.Card && (
            <>
              <Form.Item
                label="Başlık"
                name="title"
              >
                <Input placeholder="Reklam başlığı" />
              </Form.Item>

              <Form.Item
                label="Açıklama"
                name="description"
              >
                <TextArea rows={3} placeholder="Reklam açıklaması" />
              </Form.Item>
            </>
          )}

          <Form.Item
            label="Bağlantı URL (Opsiyonel)"
            name="linkUrl"
          >
            <Input placeholder="https://..." />
          </Form.Item>

          <Divider>Ödeme Bilgileri</Divider>

          {selectedSlot && (
            <Alert
              message={`Ödenecek Tutar: ₺${selectedSlot.price.toFixed(2)} (1 gün)`}
              description="Ücret seçtiğiniz tek takvim günü içindir. Talep ödeme sonrası admin onayına gider."
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}

          <CreditCard
            cardNumber={cardNumber}
            cardHolderName={cardHolderName}
            expiryDate={expiryDate}
            cvv={cvv}
            onCardNumberChange={setCardNumber}
            onCardHolderChange={setCardHolderName}
            onExpiryChange={setExpiryDate}
            onCvvChange={setCvv}
            onCvvFocus={() => {}}
            onCvvBlur={() => {}}
          />

          <Form.Item
            label="Telefon Numarası"
            name="phoneNumber"
            rules={[{ required: true, message: 'Lütfen telefon numaranızı girin' }]}
          >
            <Input placeholder="5XX XXX XX XX" />
          </Form.Item>

          <Form.Item>
            <Space wrap>
              <Button
                type="primary"
                htmlType="submit"
                loading={paymentLoading || createRequestMutation.isPending}
              >
                Ödeme Yap ve Talep Oluştur
              </Button>
              {devFakeOn && (
                <Button
                  type="dashed"
                  danger
                  htmlType="button"
                  loading={paymentLoading}
                  onClick={async () => {
                    try {
                      const values = await form.validateFields();
                      await handleDevFakeRequest(values);
                    } catch {
                      /* doğrulama hatası */
                    }
                  }}
                >
                  Test: Param olmadan tamamla
                </Button>
              )}
              <Button onClick={() => {
                setModalVisible(false);
                form.resetFields();
                setSelectedSlot(null);
                setSelectedWeek(null);
                setCardNumber('');
                setCardHolderName('');
                setExpiryDate('');
                setCvv('');
                setPaymentLoading(false);
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <SubscriptionPaymentIssueModal
        open={paymentIssueOpen}
        message={paymentIssueMessage}
        onClose={() => setPaymentIssueOpen(false)}
      />
    </>
  );
};

export default AdvertisementTab;

